import classNames from 'classnames';

import React, {
  ChangeEventHandler,
  createRef,
  forwardRef,
  ForwardRefExoticComponent,
  HTMLAttributes,
  KeyboardEventHandler,
  RefAttributes,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Error } from '../error';
import { Hint } from '../hint';

import { normalizeDigits } from './lib/digitMapping';
import styles from './code-field.scss';

type Props = RefAttributes<HTMLDivElement & HTMLInputElement> &
  HTMLAttributes<HTMLInputElement> & {
    size: number;
    maxInputSize: number;
    error?: string;
    disabled?: boolean;
    onComplete: (value: Array<string>) => void;
    value: Array<string>;
    onChange: (value: Array<string>) => void;
    dataTestId?: string;
    hint?: string;
  };

// TODO more to a better place
const usePrevious = <T,>(value: T) => {
  const ref = useRef<T>();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
};

const getValueDiff = (oldValue: string, newValue: string): string => {
  let commonLength = 0;

  while (
    commonLength < oldValue.length &&
    commonLength < newValue.length &&
    oldValue[commonLength] === newValue[commonLength]
  ) {
    commonLength++;
  }

  return newValue.slice(commonLength);
};

export const MINIMUM = 2;
export const MAXIMUM = 8;

const BASE_WIDTH = 48;

const EXPANDED_WIDTH = 104;

const COMPONENT_ID = 'modulor-code-field';

export const CodeField: ForwardRefExoticComponent<Props> = forwardRef<
  HTMLDivElement & HTMLInputElement,
  Props
>(
  (
    {
      size = 2,
      maxInputSize = 1,
      error,
      onComplete,
      className,
      disabled,
      onKeyDown: onKeyDownProps,
      value: values = [],
      onChange,
      dataTestId = COMPONENT_ID,
      hint,
      ...rest
    },
    ref,
  ) => {
    const cellSize = useMemo(() => {
      if (size < MINIMUM) return MINIMUM;
      if (size > MAXIMUM) return MAXIMUM;
      if (maxInputSize > 1 && size > 3) return 3;
      return size;
    }, [maxInputSize, size]);

    const prevCellSize = usePrevious(cellSize);

    const inputCells = useMemo(() => new Array(cellSize).fill(null), [cellSize]);

    const refs = useMemo(() => inputCells.map(() => createRef<HTMLInputElement>()), [inputCells]);

    const [controlledValue, setControlled] = useState<string[]>(() =>
      inputCells.map((_, index) => values[index] ?? ''),
    );

    useEffect(() => {
      if (prevCellSize === cellSize) return;

      setControlled(inputCells.map((_, index) => values[index] ?? ''));
    }, [prevCellSize, cellSize, inputCells, values]);

    const setNewValue = useCallback(
      (newValue: string[]) => {
        setControlled(newValue);
        onChange?.(newValue);
      },
      [onChange],
    );

    const focusOn = useCallback(
      (target: number) => {
        const el = refs[target];

        if (el && el.current) {
          el.current.setSelectionRange(0, 0);
          el.current.focus();
        }
      },
      [refs],
    );

    const focusBack = (current: number) => current !== 0 && focusOn(current - 1);

    const focusNext = (current: number) =>
      current !== inputCells.length - 1 && focusOn(current + 1);

    const handleRemoveValue = (index: number, selectionStart?: number) => {
      const newValue = controlledValue.map((value, idx) =>
        idx === index
          ? selectionStart
            ? value.slice(0, selectionStart - 1).concat(value.slice(selectionStart))
            : value.slice(0, value.length - 1)
          : value,
      );
      setNewValue?.(newValue);
    };

    const onPaste = (e) => {
      const val = e.clipboardData.getData('text/plain')?.replace(/\s/g, '');

      if (isNaN(val) && !normalizeDigits(val)) return;

      const actualValue = isNaN(val) ? normalizeDigits(val) : val;
      const newValue = controlledValue.map((_, idx) =>
        actualValue.slice(idx * maxInputSize, idx * maxInputSize + maxInputSize),
      );

      setNewValue?.(newValue);

      focusOn(actualValue.length - 1);
    };

    const onKeyDown =
      (index: number): KeyboardEventHandler<HTMLInputElement> =>
      (e) => {
        const { key } = e;
        const selectedField = refs[index].current;

        if (key === 'Backspace') {
          e.preventDefault();
          if (controlledValue[index].length && selectedField.selectionStart !== 0) {
            handleRemoveValue(index, selectedField.selectionStart);
          } else {
            handleRemoveValue(index - 1);
            focusBack(index);
          }
        }
        if (key) onKeyDownProps?.(e);
      };

    const handleChange =
      (index: number): ChangeEventHandler<HTMLInputElement> =>
      (e) => {
        const nativeEvent = e.nativeEvent as InputEvent;
        const selectedField = refs[index].current;

        // safari fire N onChange with 1 symbol and don't have nativeEvent.data, only e.target.value with full value
        // chrome fire 1 onChange with N symbol and have nativeEvent.data
        const data = (() => {
          if (nativeEvent.data) {
            return nativeEvent.data;
          }

          return getValueDiff(
            controlledValue[index].length > maxInputSize
              ? controlledValue[index].slice(maxInputSize)
              : controlledValue[index],
            e?.target?.value || '',
          );
        })();

        if (!data || !Number.isFinite(Number(data) || Number(normalizeDigits(data)))) {
          return null;
        }

        if (data.length === size && !controlledValue.every((value) => !!value)) {
          const actualValue = controlledValue.map((_, idx) =>
            data.slice(idx * maxInputSize, idx * maxInputSize + maxInputSize),
          );

          setNewValue?.(actualValue);

          return;
        }

        if (data && Number.isFinite(Number(data) || Number(normalizeDigits(data)))) {
          const newValue = controlledValue.map((value, idx) => {
            const actualValue = isNaN(Number(value)) ? normalizeDigits(value) : value;
            const actualKey = isNaN(Number(data)) ? normalizeDigits(data) : data;

            return idx === index
              ? actualValue
                  .slice(
                    0,
                    maxInputSize === selectedField.selectionStart
                      ? selectedField.selectionStart - 1
                      : selectedField.selectionStart,
                  )
                  .concat(actualKey)
                  .slice(0, maxInputSize)
              : actualValue;
          });
          setNewValue?.(newValue);

          if (maxInputSize === selectedField.selectionStart) focusNext(index);
        }

        onKeyDownProps?.(e as unknown as React.KeyboardEvent<HTMLInputElement>);
      };

    useEffect(() => {
      if (onComplete && controlledValue.join('').length === cellSize * maxInputSize) {
        onComplete(controlledValue);
      }
    }, [onComplete, cellSize, maxInputSize, controlledValue]);

    useEffect(() => {
      if (!disabled) focusOn(0);
    }, [focusOn, disabled]);

    return (
      <>
        <div
          className={classNames(className, styles.container)}
          data-id={dataTestId}
          data-testid={dataTestId}
          data-component-name={COMPONENT_ID}
          ref={ref}>
          {inputCells.map((_, index) => (
            <input
              key={index}
              className={classNames(styles.cell, {
                [styles.error]: !disabled && error,
                [styles.disabled]: disabled,
              })}
              ref={refs[index]}
              style={{
                width: maxInputSize > 1 ? `${EXPANDED_WIDTH}px` : `${BASE_WIDTH}px`,
                ...rest.style,
              }}
              value={controlledValue[index]}
              type={'tel'}
              inputMode={'numeric'}
              autoComplete={'one-time-code'}
              onPaste={onPaste}
              onKeyDown={onKeyDown(index)}
              onChange={handleChange(index)}
              data-testid={`${dataTestId}-input`}
              {...rest}
            />
          ))}
        </div>
        {!disabled ? <Error error={error} dataTestId={`${dataTestId}-error`} /> : null}
        {hint && !error ? <Hint hint={hint} dataTestId={`${dataTestId}-hint`} /> : null}
      </>
    );
  },
);

CodeField.displayName = 'CodeField';
